/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 4;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    try {
      localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
    } catch {
      // Safari Private Browsing and storage-blocked webviews throw
      // QuotaExceededError/SecurityError on write. Recently-viewed is a
      // nice-to-have; never let it break the PDP module that calls this.
    }
  }

  static clearProducts() {
    try {
      localStorage.removeItem(this.#STORAGE_KEY);
    } catch {
      // See addProduct.
    }
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   *
   * Guarded because this is called from predictive-search.js's
   * connectedCallback OUTSIDE a try/catch: a non-JSON `viewedProducts` value
   * (another app, an extension, a write truncated by a tab kill) threw there and
   * aborted the custom-element upgrade, killing the entire site search modal for
   * that visitor until they cleared storage. Also returns [] for a non-array
   * value so every caller can rely on the shape.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    try {
      const parsed = JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
}
